/*
  Native PDP behaviour: media gallery, variant selection, quantity, sticky buy bar.
  Add-to-cart itself is delegated to Dawn's <product-form>, which already talks to
  the theme's cart drawer — this file never posts to the cart itself.
*/

class MxProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-mx-gallery-track]');
    this.thumbTrack = this.querySelector('[data-mx-gallery-thumbs]');
    this.items = [...this.querySelectorAll('[data-mx-gallery-item]')];
    this.thumbs = [...this.querySelectorAll('[data-mx-gallery-thumb]')];

    if (!this.track || this.items.length === 0) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.select(Number(thumb.dataset.mxGalleryThumb)));
    });

    this.onScroll = () => {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.syncFromScroll());
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.destroyDragScroll = window.MXTheme?.initializeDragScroll?.(this.track, {
      beforeStart: () => this.cancelSmoothScroll(this.track, 'mainScrollState'),
      getDragData: () => ({ startIndex: this.getClosestIndex(this.track.scrollLeft) }),
      onDragEnd: ({ data, duration, scrollLeft, startScrollLeft }) => {
        const itemStep = this.items[1]
          ? this.items[1].offsetLeft - this.items[0].offsetLeft
          : this.items[0].offsetWidth;
        if (!itemStep) return;

        const distance = scrollLeft - startScrollLeft;
        const direction = Math.sign(distance);
        const isShortSwipe = duration < 300;
        const passedLongSwipeRatio = Math.abs(distance) / itemStep >= 0.5;
        const shouldAdvance = direction !== 0 && (isShortSwipe || passedLongSwipeRatio);
        const targetIndex = shouldAdvance ? data.startIndex + direction : data.startIndex;
        const index = Math.max(0, Math.min(this.items.length - 1, targetIndex));

        this.select(index);
      },
    });

    if (this.thumbTrack) {
      this.destroyThumbDragScroll = window.MXTheme?.initializeDragScroll?.(this.thumbTrack, {
        axis: () => this.getThumbAxis(),
        beforeStart: () => this.cancelSmoothScroll(this.thumbTrack, 'thumbScrollState'),
        onDragEnd: (detail) => this.snapThumbTrack(detail),
      });
    }

    this.mediaChangeHandler = (event) => {
      if (event.detail && event.detail.sectionId === this.dataset.sectionId) {
        this.selectByMediaId(event.detail.mediaId);
      }
    };
    document.addEventListener('mx:variant:change', this.mediaChangeHandler);

    this.setCurrent(0);
    this.initializeZoom();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.destroyDragScroll?.();
    this.destroyThumbDragScroll?.();
    this.cancelSmoothScroll(this.track, 'mainScrollState');
    this.cancelSmoothScroll(this.thumbTrack, 'thumbScrollState');
    document.removeEventListener('mx:variant:change', this.mediaChangeHandler);
    window.cancelAnimationFrame(this.frame);
    this.zoomController?.abort();
    if (this.zoomDialog?.open) this.zoomDialog.close();
  }

  initializeZoom() {
    this.zoomDialog = this.querySelector('[data-mx-gallery-dialog]');
    this.zoomTriggers = [...this.querySelectorAll('[data-mx-gallery-zoom]')];
    if (!this.zoomDialog || !this.zoomTriggers.length) return;

    this.zoomController?.abort();
    this.zoomController = new AbortController();
    const { signal } = this.zoomController;
    this.zoomView = this.querySelector('[data-mx-gallery-zoom-view]');
    this.zoomImage = this.querySelector('[data-mx-gallery-zoom-image]');
    const previous = this.querySelector('[data-mx-gallery-previous]');
    const next = this.querySelector('[data-mx-gallery-next]');
    previous.hidden = next.hidden = this.zoomTriggers.length < 2;

    // Listen above the track so its drag helper can suppress post-drag clicks.
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-mx-gallery-zoom]');
      if (!trigger || !this.contains(trigger)) return;
      this.zoomOpener = trigger;
      this.showZoomImage(this.zoomTriggers.indexOf(trigger));
      this.zoomDialog.showModal();
      this.syncZoomViewport();
    }, { signal });

    this.syncZoomViewport = () => {
      const viewport = window.visualViewport;
      if (!this.zoomDialog.open || !viewport) return;
      // CSS viewport units cannot follow the visible area during native pinch zoom.
      // Keep controls reachable while allowing the image itself to magnify and pan.
      const style = this.querySelector('[data-mx-gallery-controls]').style;
      style.setProperty('--mx-gallery-controls-width', `${viewport.width * viewport.scale}px`);
      style.setProperty('--mx-gallery-controls-height', `${viewport.height * viewport.scale}px`);
      style.setProperty('--mx-gallery-controls-x', `${viewport.offsetLeft}px`);
      style.setProperty('--mx-gallery-controls-y', `${viewport.offsetTop}px`);
      style.setProperty('--mx-gallery-controls-scale', String(1 / viewport.scale));
    };
    window.visualViewport?.addEventListener('resize', this.syncZoomViewport, { signal });
    window.visualViewport?.addEventListener('scroll', this.syncZoomViewport, { signal });

    this.querySelector('[data-mx-gallery-close]').addEventListener('click', () => this.zoomDialog.close(), { signal });
    previous.addEventListener('click', () => this.showZoomImage(this.zoomIndex - 1), { signal });
    next.addEventListener('click', () => this.showZoomImage(this.zoomIndex + 1), { signal });
    this.zoomView.addEventListener('click', (event) => {
      if (!this.suppressZoomClick) {
        this.setZoomed(!this.zoomed, event.detail ? { x: event.clientX, y: event.clientY } : null);
      }
      this.suppressZoomClick = false;
    }, { signal });
    this.zoomImage.addEventListener('load', () => this.scrollToZoomPoint(), { signal });
    this.zoomView.addEventListener('pointerdown', (event) => {
      this.suppressZoomClick = false;
      this.zoomSwipe = event.pointerType === 'touch' && event.isPrimary && !this.zoomed
        ? { x: event.clientX, y: event.clientY } : null;
    }, { signal });
    this.zoomView.addEventListener('pointerup', (event) => {
      if (!this.zoomSwipe) return;
      const dx = event.clientX - this.zoomSwipe.x;
      const dy = event.clientY - this.zoomSwipe.y;
      this.zoomSwipe = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 2) {
        this.suppressZoomClick = true;
        this.showZoomImage(this.zoomIndex + (dx < 0 ? 1 : -1));
      }
    }, { signal });
    this.zoomView.addEventListener('pointercancel', () => { this.zoomSwipe = null; }, { signal });
    this.zoomDialog.addEventListener('click', (event) => {
      if (event.target === this.zoomDialog) this.zoomDialog.close();
    }, { signal });
    this.zoomDialog.addEventListener('close', () => {
      this.setZoomed(false);
      this.zoomImage.removeAttribute('src');
      this.zoomOpener?.focus({ preventScroll: true });
    }, { signal });
    this.zoomDialog.addEventListener('keydown', (event) => {
      if (!this.zoomed && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        this.showZoomImage(this.zoomIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }
      if (event.key === 'Tab') {
        const buttons = [...this.zoomDialog.querySelectorAll('button')].filter((button) => !button.hidden);
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }, { signal });
  }

  showZoomImage(index) {
    this.zoomIndex = (index + this.zoomTriggers.length) % this.zoomTriggers.length;
    const trigger = this.zoomTriggers[this.zoomIndex];
    const source = trigger.querySelector('img');
    this.zoomImage.alt = source.alt;
    this.setZoomed(false);
    this.zoomImage.width = Number(source.getAttribute('width'));
    this.zoomImage.height = Number(source.getAttribute('height'));
    this.zoomImage.src = trigger.dataset.mxGalleryZoom;
    this.querySelector('[data-mx-gallery-count]').textContent = `${this.zoomIndex + 1} / ${this.zoomTriggers.length}`;
  }

  setZoomed(zoomed, point = null) {
    if (zoomed) {
      const image = this.zoomImage.getBoundingClientRect();
      const view = this.zoomView.getBoundingClientRect();
      const width = this.zoomImage.naturalWidth || this.zoomImage.width;
      const height = this.zoomImage.naturalHeight || this.zoomImage.height;
      const scale = Math.min(image.width / width, image.height / height);
      const x = point?.x ?? image.left + image.width / 2;
      const y = point?.y ?? image.top + image.height / 2;
      // Account for the empty space around an object-fit: contain image.
      this.zoomPoint = {
        x: Math.max(0, Math.min(1, (x - image.left - (image.width - width * scale) / 2) / (width * scale))),
        y: Math.max(0, Math.min(1, (y - image.top - (image.height - height * scale) / 2) / (height * scale))),
        offsetX: x - view.left,
        offsetY: y - view.top,
      };
    } else {
      this.zoomPoint = null;
    }
    this.zoomed = zoomed;
    this.zoomView.classList.toggle('is-zoomed', zoomed);
    this.zoomView.setAttribute('aria-pressed', String(zoomed));
    this.zoomView.setAttribute('aria-label', `${zoomed ? 'Zoom out' : 'Zoom in'}: ${this.zoomImage.alt}`);
    if (zoomed) this.scrollToZoomPoint();
    else this.zoomView.scrollTo(0, 0);
  }

  scrollToZoomPoint() {
    if (!this.zoomed || !this.zoomPoint) return;
    const image = this.zoomImage.getBoundingClientRect();
    const view = this.zoomView.getBoundingClientRect();
    // Keep the selected image detail under the pointer, including after image load.
    this.zoomView.scrollTo(
      this.zoomView.scrollLeft + image.left - view.left + image.width * this.zoomPoint.x - this.zoomPoint.offsetX,
      this.zoomView.scrollTop + image.top - view.top + image.height * this.zoomPoint.y - this.zoomPoint.offsetY
    );
  }

  /* Keep thumbnail clicks, variant media changes, and drag snapping on one rail. */
  select(index) {
    const item = this.items[index];
    if (!item) return;

    if (this.track.scrollWidth > this.track.clientWidth + 1) {
      const targetLeft = this.getItemScrollPosition(item);
      this.smoothScrollTo(this.track, targetLeft, 'mainScrollState', () => this.setCurrent(index));
    } else {
      item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    this.setCurrent(index);
  }

  selectByMediaId(mediaId) {
    if (!mediaId) return;
    const index = this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
    if (index >= 0) this.select(index);
  }

  syncFromScroll() {
    if (this.track.classList.contains('is-snapping')) return;
    this.setCurrent(this.getClosestIndex(this.track.scrollLeft));
  }

  getThumbAxis() {
    if (!this.thumbTrack) return 'x';
    return window.getComputedStyle(this.thumbTrack).flexDirection === 'column' ? 'y' : 'x';
  }

  snapThumbTrack(detail) {
    if (!this.thumbTrack) return;

    const axis = this.getThumbAxis();
    const scrollPosition = axis === 'y' ? detail.scrollTop : detail.scrollLeft;
    const positions = this.thumbs.map((thumb) => {
      const item = thumb.parentElement;
      return axis === 'y'
        ? item.offsetTop - this.thumbTrack.offsetTop
        : item.offsetLeft - this.thumbTrack.offsetLeft;
    });
    const targetPosition = positions.reduce((closest, position) =>
      Math.abs(position - scrollPosition) < Math.abs(closest - scrollPosition) ? position : closest
    );

    this.smoothScrollTo(this.thumbTrack, targetPosition, 'thumbScrollState', () => {}, axis);
  }

  smoothScrollTo(track, position, stateKey, onComplete = () => {}, axis = 'x') {
    if (!track) return;

    this.cancelSmoothScroll(track, stateKey);
    const maxScroll = Math.max(
      0,
      axis === 'y' ? track.scrollHeight - track.clientHeight : track.scrollWidth - track.clientWidth
    );
    const targetPosition = Math.max(0, Math.min(maxScroll, position));
    const currentPosition = axis === 'y' ? track.scrollTop : track.scrollLeft;
    const scrollOptions = axis === 'y' ? { top: targetPosition } : { left: targetPosition };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || Math.abs(currentPosition - targetPosition) <= 1) {
      track.scrollTo({ ...scrollOptions, behavior: 'auto' });
      onComplete();
      return;
    }

    track.classList.add('is-snapping');
    let fallbackTimer;
    const cleanup = () => {
      track.removeEventListener('scrollend', finish);
      window.clearTimeout(fallbackTimer);
      track.classList.remove('is-snapping');
      this[stateKey] = null;
    };
    const finish = () => {
      cleanup();
      onComplete();
    };

    track.addEventListener('scrollend', finish);
    fallbackTimer = window.setTimeout(finish, 1_000);
    this[stateKey] = { cleanup };
    track.scrollTo({ ...scrollOptions, behavior: 'smooth' });
  }

  cancelSmoothScroll(track, stateKey) {
    if (!track) return;

    const state = this[stateKey];
    if (!state) return;

    const { scrollLeft, scrollTop } = track;
    state.cleanup();
    track.scrollTo({ left: scrollLeft, top: scrollTop, behavior: 'auto' });
  }

  getClosestIndex(scrollLeft) {
    let closest = 0;
    let smallest = Infinity;

    this.items.forEach((item, index) => {
      const distance = Math.abs(this.getItemScrollPosition(item) - scrollLeft);
      if (distance < smallest) {
        smallest = distance;
        closest = index;
      }
    });

    return closest;
  }

  getItemScrollPosition(item) {
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const itemStart = item.offsetLeft - this.track.offsetLeft;

    /*
      Desktop shows two media at once, so the last two items share the same
      reachable scroll position. Clamp before comparing to keep the lower
      (leading) item as the drag origin and allow a gesture back from the end.
    */
    return Math.max(0, Math.min(maxScroll, itemStart));
  }

  setCurrent(index) {
    if (this.current === index) return;
    this.current = index;

    this.thumbs.forEach((thumb) => {
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.mxGalleryThumb) === index));
    });

    this.keepThumbVisible(index);
  }

  keepThumbVisible(index) {
    if (!this.thumbTrack || this.thumbTrack.classList.contains('is-dragging')) return;

    const item = this.thumbs[index]?.parentElement;
    if (!item) return;

    const axis = this.getThumbAxis();
    const itemStart = axis === 'y'
      ? item.offsetTop - this.thumbTrack.offsetTop
      : item.offsetLeft - this.thumbTrack.offsetLeft;
    const itemEnd = itemStart + (axis === 'y' ? item.offsetHeight : item.offsetWidth);
    const visibleStart = axis === 'y' ? this.thumbTrack.scrollTop : this.thumbTrack.scrollLeft;
    const visibleEnd = visibleStart + (axis === 'y' ? this.thumbTrack.clientHeight : this.thumbTrack.clientWidth);
    let targetPosition = null;

    if (itemStart < visibleStart) {
      targetPosition = itemStart;
    } else if (itemEnd > visibleEnd) {
      targetPosition = itemEnd - (axis === 'y' ? this.thumbTrack.clientHeight : this.thumbTrack.clientWidth);
    }

    if (targetPosition !== null) {
      this.smoothScrollTo(this.thumbTrack, targetPosition, 'thumbScrollState', () => {}, axis);
    }
  }
}

if (!customElements.get('mx-product-gallery')) {
  customElements.define('mx-product-gallery', MxProductGallery);
}

const MX_GROWAVE_CARD_STYLE_ID = 'mx-growave-card-a11y';
const MX_GROWAVE_CARD_STYLE = `
  .gw-toggle-wishlist-product-card-button__counter {
    background-color: #d91f42 !important;
  }
`;

class MxPdpAppState extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('[data-mx-pdp-section]');
    if (!this.root) return;

    this.shadowObservers = new Map();

    this.hasRenderedContent = (element) =>
      Boolean(
        element.textContent.trim() ||
          element.children.length ||
          element.shadowRoot?.children.length,
      );

    this.syncShopTheLookVariants = () => {
      this.root.querySelectorAll('.js-ctl-product-component').forEach((component) => {
        const selectedVariant = component.querySelector(
          '.variant-button.active:not(.disabled)[data-variant-size-id]',
        );
        const variantInput = component.querySelector('input[id^="variant-size-id-"]');
        const variantId = selectedVariant?.dataset.variantSizeId;

        if (variantInput && variantId && variantInput.value !== variantId) {
          variantInput.value = variantId;
        }
      });
    };

    this.update = () => {
      this.root.querySelectorAll('.shopthelook_add-all-to-cart').forEach((button) => {
        const hasContent = this.hasRenderedContent(button);
        button.toggleAttribute('data-mx-has-content', hasContent);
        button.toggleAttribute('hidden', !hasContent);
      });

      this.syncShopTheLookVariants();
    };

    this.onShopTheLookCartClick = (event) => {
      if (!event.target.closest('.shopthelook_add-to-cart, .shopthelook_add-all-to-cart')) return;

      // Shop The Look can visually select an available size while retaining its sold-out first variant ID.
      this.syncShopTheLookVariants();
    };

    this.observeShadowRoot = (shadowRoot, update) => {
      if (this.shadowObservers.has(shadowRoot)) return;

      const observer = new MutationObserver(update);
      observer.observe(shadowRoot, { childList: true, subtree: true });
      this.shadowObservers.set(shadowRoot, observer);
    };

    this.labelCustomerHubControls = (shadowRoot) => {
      const carousels = [
        ['for-you-collections', 'recommended collections'],
        ['recently-viewed', 'recently viewed products'],
        ['cart-saved', 'saved cart products'],
      ];

      for (const [id, label] of carousels) {
        shadowRoot.querySelector(`#${id} .ar-l`)?.setAttribute('aria-label', `Previous ${label}`);
        shadowRoot.querySelector(`#${id} .ar-r`)?.setAttribute('aria-label', `Next ${label}`);
      }
    };

    this.applyThirdPartyAccessibility = () => {
      let pending = false;

      document.querySelectorAll('.gw-add-to-wishlist-product-card-placeholder').forEach((host) => {
        const shadowRoot = host.shadowRoot;
        if (!shadowRoot) {
          pending = true;
          return;
        }

        if (shadowRoot.getElementById(MX_GROWAVE_CARD_STYLE_ID)) return;

        const style = document.createElement('style');
        style.id = MX_GROWAVE_CARD_STYLE_ID;
        style.textContent = MX_GROWAVE_CARD_STYLE;
        shadowRoot.append(style);
      });

      const customerHubRoot = document.querySelector('accounts-everywhere')?.shadowRoot;
      if (!customerHubRoot) return false;

      this.labelCustomerHubControls(customerHubRoot);
      this.observeShadowRoot(customerHubRoot, () => this.labelCustomerHubControls(customerHubRoot));
      return !pending;
    };

    this.stopThirdPartyObserver = () => {
      this.thirdPartyObserver?.disconnect();
      this.thirdPartyObserver = null;
      window.clearTimeout(this.thirdPartyObserverTimeout);
      this.thirdPartyObserverTimeout = null;
    };

    this.update();
    this.observer = new MutationObserver(this.update);
    this.observer.observe(this.root, { childList: true, characterData: true, subtree: true });
    this.root.addEventListener('click', this.onShopTheLookCartClick, true);

    this.onThirdPartyMutation = () => {
      if (this.applyThirdPartyAccessibility()) this.stopThirdPartyObserver();
    };

    if (!this.applyThirdPartyAccessibility()) {
      this.thirdPartyObserver = new MutationObserver(this.onThirdPartyMutation);
      this.thirdPartyObserver.observe(document.documentElement, { childList: true, subtree: true });
      this.thirdPartyObserverTimeout = window.setTimeout(() => {
        this.applyThirdPartyAccessibility();
        this.stopThirdPartyObserver();
      }, 10_000);
      window.addEventListener('load', this.onThirdPartyMutation, { once: true });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.stopThirdPartyObserver?.();
    this.shadowObservers?.forEach((observer) => observer.disconnect());
    window.removeEventListener('load', this.onThirdPartyMutation);
    this.root?.removeEventListener('click', this.onShopTheLookCartClick, true);
  }
}

if (!customElements.get('mx-pdp-app-state')) {
  customElements.define('mx-pdp-app-state', MxPdpAppState);
}

class MxProductOptions extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-mx-variants]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch (error) {
      this.variants = [];
    }

    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;
    this.updateUrl = this.dataset.updateUrl !== 'false';

    this.inputs = [...this.querySelectorAll('[data-mx-option-input]')];
    this.select = this.querySelector('[data-mx-option-select]');

    this.error = this.querySelector('[data-mx-option-error]');

    this.inputs.forEach((input) => input.addEventListener('change', () => this.onChange()));
    this.select?.addEventListener('change', () => this.onChange());

    // No size is preselected: block add to cart until the shopper picks one.
    // Capture phase runs before product-form's own submit handler on the same form.
    this.form = this.hasAttribute('data-require-choice') ? this.scope('form[data-type="add-to-cart-form"]') : null;
    this.onSubmit = (event) => {
      if (this.hasSelection) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError();
    };
    this.form?.addEventListener('submit', this.onSubmit, true);

    // Restored form state (back/forward cache) must sync the hidden variant id.
    const idInput = this.scope('[data-mx-variant-id]');
    const restored = this.select?.value || this.inputs.some((input) => input.checked);
    if (idInput && !idInput.value && restored) this.onChange();
    else if (idInput && !idInput.value) this.restoreChosenVariant();
  }

  get chosenKey() {
    return `mx-chosen-variant:${this.productUrl}`;
  }

  // Reselect a size after reload only when this tab's shopper picked it;
  // a ?variant= from an ad or shared link alone never preselects.
  restoreChosenVariant() {
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    let chosen = null;
    try {
      chosen = sessionStorage.getItem(this.chosenKey);
    } catch (error) {
      return;
    }
    if (!urlVariant || urlVariant !== chosen) return;

    const variant = this.variants.find((candidate) => String(candidate.id) === urlVariant);
    const value = variant?.options[Number(this.dataset.optionIndex)];
    if (!variant?.available || value === undefined) return;

    if (this.select) {
      this.select.value = value;
    } else {
      const input = this.inputs.find((candidate) => candidate.value === value && !candidate.disabled);
      if (!input) return;
      input.checked = true;
    }
    this.onChange();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit, true);
  }

  // A matching variant id, not just a checked input: a size with no variant
  // for the current colour leaves the id empty.
  get hasSelection() {
    return Boolean(this.scope('[data-mx-variant-id]')?.value);
  }

  setInvalid(invalid) {
    (this.select ? [this.select] : this.inputs).forEach((control) => {
      if (invalid) control.setAttribute('aria-invalid', 'true');
      else control.removeAttribute('aria-invalid');
    });
  }

  showError() {
    if (this.error) {
      // Re-insert the text so screen readers announce repeat attempts too.
      const message = this.error.textContent;
      this.error.textContent = '';
      this.error.hidden = false;
      requestAnimationFrame(() => {
        this.error.textContent = message;
      });
    }
    this.setInvalid(true);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    const target = this.select || this.inputs.find((input) => !input.disabled);
    target?.focus({ preventScroll: true });
  }

  get selectedValues() {
    if (this.select) {
      const values = this.dataset.baseOptions ? JSON.parse(this.dataset.baseOptions) : [];
      values[Number(this.dataset.optionIndex)] = this.select.value;
      return values;
    }

    const values = this.dataset.baseOptions ? JSON.parse(this.dataset.baseOptions) : [];
    const checked = this.inputs.find((input) => input.checked);
    if (checked) values[Number(this.dataset.optionIndex)] = checked.value;
    return values;
  }

  onChange() {
    const values = this.selectedValues;
    const variant = this.variants.find((candidate) =>
      candidate.options.every((option, index) => values[index] === undefined || option === values[index]),
    );

    if (!variant) {
      const input = this.scope('[data-mx-variant-id]');
      if (input) input.value = '';
      return;
    }

    if (this.error) this.error.hidden = true;
    this.setInvalid(false);
    this.updateIdInput(variant);
    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.updateAddress(variant);
    try {
      sessionStorage.setItem(this.chosenKey, String(variant.id));
    } catch (error) {
      // Storage can be unavailable (private mode quotas); reload then simply shows no size.
    }

    document.dispatchEvent(
      new CustomEvent('mx:variant:change', {
        detail: { sectionId: this.sectionId, variant, mediaId: variant.featured_media_id },
      }),
    );
  }

  scope(selector) {
    return document.querySelector(`[data-mx-pdp-section="${this.sectionId}"] ${selector}`);
  }

  updateIdInput(variant) {
    const input = this.scope('[data-mx-variant-id]');
    if (!input) return;
    input.value = variant.id;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updatePrice(variant) {
    const price = this.scope('[data-mx-price]');
    const compare = this.scope('[data-mx-compare-price]');
    const badge = this.scope('[data-mx-price-badge]');
    const badgeValue = this.scope('[data-mx-price-badge-value]');

    if (price) price.innerHTML = variant.price_formatted;

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (compare) {
      compare.innerHTML = onSale ? variant.compare_at_price_formatted : '';
      compare.hidden = !onSale;
    }

    if (badge) badge.hidden = !onSale;

    if (badgeValue && onSale) {
      // Integer division, matching the Liquid-rendered value.
      const saved = Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
      badgeValue.textContent = `${saved}%`;
    }

    const stickyPrice = this.scope('[data-mx-sticky-price]');
    if (stickyPrice) stickyPrice.innerHTML = variant.price_formatted;

    const payment = this.scope('[data-mx-payment-instalment]');
    if (payment && variant.instalment_formatted) payment.innerHTML = variant.instalment_formatted;
  }

  updateAvailability(variant) {
    const button = this.scope('[data-mx-add-button]');
    if (!button) return;

    const label = button.querySelector('[data-mx-add-label]');
    button.disabled = !variant.available;

    if (label) {
      label.textContent = variant.available ? button.dataset.labelAvailable : button.dataset.labelSoldOut;
    }
  }

  updateAddress(variant) {
    if (!this.updateUrl) return;
    // Keep every other query parameter (view, utm, …) — only the variant changes.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }
}

if (!customElements.get('mx-product-options')) {
  customElements.define('mx-product-options', MxProductOptions);
}

class MxProductSwatches extends HTMLElement {
  connectedCallback() {
    this.applySwatchSemantics = () => {
      document.querySelectorAll('.pl-swatches__link[data-handle]').forEach((link) => {
        const handle = link.dataset.handle?.trim();
        const href = link.getAttribute('href') || '';

        if (handle && /^[a-z0-9-]+$/i.test(handle) && (!href || href.startsWith('javascript:'))) {
          link.href = `/products/${handle}`;
          link.removeAttribute('onclick');
        }
      });

      this.querySelectorAll('a img').forEach((image) => {
        if (image.getAttribute('alt')?.trim()) return;

        const link = image.closest('a');
        const label = link?.getAttribute('title')?.trim() || link?.getAttribute('aria-label')?.trim() || 'Product';
        image.alt = `${label} color swatch`;
      });
    };

    this.applySwatchSemantics();

    // Product Links renders and replaces these swatches asynchronously.
    this.observer = new MutationObserver(this.applySwatchSemantics);
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('mx-product-swatches')) {
  customElements.define('mx-product-swatches', MxProductSwatches);
}

class MxProductWishlist extends HTMLElement {
  connectedCallback() {
    this.placeholder = this.querySelector('.gw-wl-add-to-wishlist-placeholder');
    this.applyAccessibleName = () => {
      const shadowRoot = this.placeholder?.shadowRoot;
      const button = shadowRoot?.querySelector('button');
      if (!shadowRoot || !button) return false;

      const inWishlist = button.dataset.inWishlist === 'true';
      button.setAttribute('aria-label', inWishlist ? this.dataset.labelRemove : this.dataset.labelAdd);
      // Growave fixes this shadow-DOM control at 47px; match the native buy-row contract.
      button.style.height = 'var(--mx-pdp-buy-control-size)';
      button.style.minWidth = 'var(--mx-pdp-buy-control-size)';

      if (!this.shadowObserver) {
        // Growave changes the button state inside its open shadow root.
        this.shadowObserver = new MutationObserver(this.applyAccessibleName);
        this.shadowObserver.observe(shadowRoot, {
          attributes: true,
          attributeFilter: ['data-in-wishlist'],
          childList: true,
          subtree: true,
        });
      }

      window.clearTimeout(this.observerTimeout);
      this.observerTimeout = null;
      this.documentObserver?.disconnect();
      this.documentObserver = null;
      return true;
    };

    if (this.applyAccessibleName()) return;

    // Growave attaches its shadow root asynchronously after the placeholder renders.
    this.documentObserver = new MutationObserver(this.applyAccessibleName);
    this.documentObserver.observe(document.documentElement, { childList: true, subtree: true });
    this.observerTimeout = window.setTimeout(() => {
      this.applyAccessibleName();
      this.documentObserver?.disconnect();
      this.documentObserver = null;
      this.observerTimeout = null;
    }, 10_000);
    window.addEventListener('load', this.applyAccessibleName, { once: true });
  }

  disconnectedCallback() {
    window.clearTimeout(this.observerTimeout);
    this.documentObserver?.disconnect();
    this.shadowObserver?.disconnect();
    this.observerTimeout = null;
    this.documentObserver = null;
    this.shadowObserver = null;
    window.removeEventListener('load', this.applyAccessibleName);
  }
}

if (!customElements.get('mx-product-wishlist')) {
  customElements.define('mx-product-wishlist', MxProductWishlist);
}

class MxSizeChart extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-mx-size-chart-trigger]');
    this.dialog = this.querySelector('[data-mx-size-chart-dialog]');
    this.closeButtons = [...this.querySelectorAll('[data-mx-size-chart-close]')];
    if (!this.trigger || !this.dialog) return;

    this.open = () => {
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('mxsc-lock');
      this.dialog.querySelector('[data-mx-size-chart-close]')?.focus();
    };
    this.close = () => this.dialog.close();
    this.onBackdropClick = (event) => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) this.close();
    };
    this.onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = this.closeButtons.filter((button) => button.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    this.onDialogClose = () => {
      document.documentElement.classList.remove('mxsc-lock');
      this.trigger.focus();
    };

    this.trigger.addEventListener('click', this.open);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
    this.dialog.addEventListener('click', this.onBackdropClick);
    this.dialog.addEventListener('keydown', this.onKeydown);
    this.dialog.addEventListener('close', this.onDialogClose);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.open);
    this.closeButtons?.forEach((button) => button.removeEventListener('click', this.close));
    this.dialog?.removeEventListener('click', this.onBackdropClick);
    this.dialog?.removeEventListener('keydown', this.onKeydown);
    this.dialog?.removeEventListener('close', this.onDialogClose);
    if (this.dialog?.open) document.documentElement.classList.remove('mxsc-lock');
  }
}

if (!customElements.get('mx-size-chart')) {
  customElements.define('mx-size-chart', MxSizeChart);
}

class MxQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-mx-quantity-input]');
    if (!this.input) return;

    this.querySelectorAll('[data-mx-quantity-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = Number(button.dataset.mxQuantityStep);
        const min = Number(this.input.min) || 1;
        const max = Number(this.input.max) || Infinity;
        const next = Math.min(max, Math.max(min, (Number(this.input.value) || min) + step));
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
}

if (!customElements.get('mx-quantity')) {
  customElements.define('mx-quantity', MxQuantity);
}

class MxStickyBuy extends HTMLElement {
  connectedCallback() {
    // The details column is sticky, so the real buy row never scrolls away.
    // Like the previous build, the bar appears past a scroll offset instead.
    this.offset = Number(this.dataset.offset) || 900;

    this.update = () => {
      this.frame = null;
      this.dataset.visible = String(window.scrollY > this.offset);
    };

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(this.update);
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.frame) window.cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('mx-sticky-buy')) {
  customElements.define('mx-sticky-buy', MxStickyBuy);
}
